import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

const SettingsAdmins = () => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', password: '' });
  const queryClient = useQueryClient();
  const apiUrl = process.env.REACT_APP_API_URL;

  const { 
    data: admins = [], 
    isLoading, 
    isError, 
    error 
  } = useQuery({
    queryKey: ['admins'],
    queryFn: async () => {
      try {
        const response = await fetch(`${apiUrl}/api/users`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          },
        });
        
        if (!response.ok) {
          if (response.status === 401) {
            throw new Error('غير مصرح لك بالوصول - يرجى تسجيل الدخول');
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        return data;
      } catch (err) {
        console.error('Fetch error:', err);
        throw new Error(`Failed to fetch users: ${err.message}`);
      }
    },
    retry: 1,
    refetchOnWindowFocus: false,
  });

  const addAdminMutation = useMutation({
    mutationFn: async (newAdmin) => {
      const response = await fetch(`${apiUrl}/api/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newAdmin),
      });
      if (!response.ok) {
        throw new Error('Failed to add admin');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admins'] });
      setFormData({ name: '', email: '', password: '' });
      setShowAddForm(false);
    },
    onError: (error) => {
      alert('خطأ في إضافة المشرف: ' + error.message);
    }
  });

  const deleteAdminMutation = useMutation({
    mutationFn: async (id) => {
      const response = await fetch(`${apiUrl}/api/users/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
      });
      if (!response.ok) {
        throw new Error('Failed to delete admin');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admins'] });
    },
    onError: (error) => {
      alert('خطأ في حذف المشرف: ' + error.message);
    }
  });

  const handleAddClick = () => {
    setShowAddForm(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = () => {
    if (!formData.name || !formData.email || !formData.password) {
      alert('يرجى ملء جميع الحقول');
      return;
    }
    
    addAdminMutation.mutate({
      name: formData.name,
      email: formData.email,
      password: formData.password,
      role: 'super_admin'
    });
  };

  const handleDelete = (id) => {
    if (window.confirm('هل أنت متأكد من حذف المشرف؟')) {
      deleteAdminMutation.mutate(id);
    }
  };

  if (isLoading) {
    return (
      <div className="dashboard-container" style={{ padding: '20px' }}>
        <div className="dashboard-card">
          <div className="dashboard-card-body" style={{ textAlign: 'center', padding: '2rem' }}>
            جاري التحميل...
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="dashboard-container" style={{ padding: '20px' }}>
        <div className="dashboard-card">
          <div className="dashboard-card-body" style={{ textAlign: 'center', padding: '2rem', color: '#dc2626' }}>
            خطأ في تحميل البيانات: {error?.message || 'حدث خطأ غير معروف'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container" style={{ padding: '20px' }}>
      <div className="dashboard-card">
        <div className="dashboard-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3>إعدادات المشرفين</h3>
          {!showAddForm && (
            <button 
              className="add-btn" 
              onClick={handleAddClick}
              disabled={addAdminMutation.isPending}
            >
              إضافة
            </button>
          )}
        </div>

        {showAddForm && (
          <div className="dashboard-card-body">
            <div style={{ direction: 'rtl' }}>
              <div className="dashboard-form-group">
                <label className="dashboard-form-label" htmlFor="name">الاسم</label>
                <input
                  className="dashboard-form-input"
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="ادخل الاسم"
                  autoComplete="off"
                  disabled={addAdminMutation.isPending}
                />
              </div>
              <div className="dashboard-form-group">
                <label className="dashboard-form-label" htmlFor="email">البريد الإلكتروني</label>
                <input
                  className="dashboard-form-input"
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="ادخل البريد الإلكتروني"
                  autoComplete="off"
                  disabled={addAdminMutation.isPending}
                />
              </div>
              <div className="dashboard-form-group">
                <label className="dashboard-form-label" htmlFor="password">كلمة المرور</label>
                <input
                  className="dashboard-form-input"
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="ادخل كلمة المرور"
                  autoComplete="new-password"
                  disabled={addAdminMutation.isPending}
                />
              </div>
              <div className="dashboard-form-actions">
                <button 
                  onClick={handleFormSubmit}
                  className="dashboard-btn dashboard-btn-primary"
                  disabled={addAdminMutation.isPending}
                >
                  {addAdminMutation.isPending ? 'جاري الحفظ...' : 'حفظ'}
                </button>
                <button
                  type="button"
                  className="dashboard-btn dashboard-btn-secondary"
                  onClick={() => setShowAddForm(false)}
                  disabled={addAdminMutation.isPending}
                >
                  إلغاء
                </button>
              </div>
            </div>
          </div>
        )}

        {!showAddForm && (
          <div className="dashboard-card-body table-container">
            <table className="table" style={{ direction: 'rtl' }}>
              <thead>
                <tr>
                  <th>الاسم</th>
                  <th>البريد الإلكتروني</th>
                  <th>الدور</th>
                  <th style={{ width: 120, textAlign: 'center' }}>إجراء</th>
                </tr>
              </thead>
              <tbody>
                {admins.length === 0 ? (
                  <tr>
                    <td colSpan="4" style={{ textAlign: 'center', padding: '1rem' }}>لا يوجد مشرفون حالياً.</td>
                  </tr>
                ) : (
                  admins.map((admin) => (
                    <tr key={admin.id}>
                      <td>{admin.name}</td>
                      <td>{admin.email}</td>
                      <td>{admin.role === 'super_admin' ? 'مشرف عام' : admin.role}</td>
                      <td style={{ textAlign: 'center' }}>
                        <button
                          onClick={() => handleDelete(admin.id)}
                          className="dashboard-btn dashboard-btn-secondary"
                          style={{ 
                            backgroundColor: '#dc2626', 
                            borderColor: '#dc2626', 
                            color: 'white', 
                            padding: '6px 12px',
                            opacity: deleteAdminMutation.isPending ? 0.6 : 1
                          }}
                          disabled={deleteAdminMutation.isPending}
                        >
                          {deleteAdminMutation.isPending ? 'جاري الحذف...' : 'حذف'}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SettingsAdmins;